import './style/chromeExtension.scss';

import React from 'react';
import {useTranslation} from 'react-i18next';
import {Icon} from '@iconify/react';
import Image from '../misc/image';
import {BluemateButton} from '../controls';
import {CHROME_EXTENSION_STORE_URL} from '../../util/constants';
import {AwesomeIcon} from '@dvrd/dvr-controls';

interface Props {
    onShare: VoidFunction;
}

interface Feature {
    id: string;
    icon: string;
    title: string;
    text: string;
}

export default function ChromeExtension(props: Props) {
    const {onShare} = props;
    const {t} = useTranslation();

    const features: Feature[] = [
        {
            id: 'sales',
            icon: 'mdi:chart-line',
            title: t('chromeExtension.featureSalesTitle', 'Inschatting'),
            text: t(
                'chromeExtension.featureSalesText',
                'Ons model berekent het aantal verkopen en de omzet per maand voor producten op bol. Hiervoor gebruikt het publieke metadata van de producten, gegeven door bol.'
            )
        },
        {
            id: 'everywhere',
            icon: 'mdi:magnify',
            title: t('chromeExtension.featureEverywhereTitle', 'In de zoekresultaten'),
            text: t(
                'chromeExtension.featureEverywhereText',
                'De schatting verschijnt in de zoekresultaten en op de productpagina van bol.com zelf.'
            )
        },
        {
            id: 'free',
            icon: 'mdi:gift-outline',
            title: t('chromeExtension.featureFreeTitle', 'Publiek en gratis'),
            text: t(
                'chromeExtension.featureFreeText',
                'Onze Chrome Extensie is gratis voor iedereen - je hebt geen Bluemate noch bol account nodig. Deel hem met je vrienden!'
            )
        }
    ];

    function renderStoreButton(label: string) {
        return (
            <BluemateButton
                className='store-button'
                href={CHROME_EXTENSION_STORE_URL}
                label={label}
                ornaments={{element: <Icon icon='logos:chrome' className='store-button-logo' />}}
            />
        );
    }

    function renderShareButton() {
        return (
            <BluemateButton
                className='share-button'
                onClick={onShare}
                label={t('chromeExtension.shareButton', 'Share de link')}
                ornaments={{element: <AwesomeIcon name='copy' />}}
                secondary
            />
        );
    }

    function renderCta() {
        return (
            <div className='store-cta'>
                <div className='store-cta-buttons'>
                    {renderStoreButton(t('chromeExtension.storeButton', 'Download de Chrome Extensie'))}
                    {renderShareButton()}
                </div>
                <span className='store-cta-note'>
                    <Icon icon='mdi:check-circle-outline' className='store-cta-note-icon' />
                    {t('chromeExtension.ctaNote', 'Gratis en publiek — geen Bluemate of Bol account nodig')}
                </span>
            </div>
        );
    }

    function renderHero() {
        return (
            <header className='extension-hero'>
                <div className='hero-text'>
                    <span className='hero-eyebrow'>{t('chromeExtension.eyebrow', 'Chrome Extensie')}</span>
                    <h1 className='hero-title'>{t('chromeExtension.title', 'De omzet van elk product op bol.com')}</h1>
                    <p className='hero-intro'>
                        {t('chromeExtension.intro', 'Een inschatting van de omzet en orders van elk product op bol.')}
                        <br />
                        {t(
                            'chromeExtension.introDiscover',
                            'Ontdek potentiele kansen met deze productonderzoek tool op bol.com.'
                        )}
                    </p>
                    {renderCta()}
                </div>
                <div className='hero-visual'>
                    <Image name='logo-no-text-big' alt='' className='hero-logo' />
                </div>
            </header>
        );
    }

    function renderFeature(feature: Feature) {
        return (
            <div key={feature.id} className='feature-card'>
                <Icon icon={feature.icon} className='feature-icon' />
                <h3 className='feature-title'>{feature.title}</h3>
                <p className='feature-text'>{feature.text}</p>
            </div>
        );
    }

    function renderFeatures() {
        return (
            <section className='features-section'>
                <h2 className='section-heading'>{t('chromeExtension.featuresTitle', 'Wat doet de extensie?')}</h2>
                <div className='features-grid'>{features.map(renderFeature)}</div>
            </section>
        );
    }

    function renderShot(name: string, alt: string, caption: string) {
        return (
            <figure className='shot-card'>
                <div className='shot-frame'>
                    <Image name={name} alt={alt} />
                </div>
                <figcaption>{caption}</figcaption>
            </figure>
        );
    }

    function renderScreenshots() {
        return (
            <section className='shots-section'>
                <h2 className='section-heading'>{t('chromeExtension.shotsTitle', 'Zo ziet het eruit')}</h2>
                {renderShot(
                    'chrome-ext-list',
                    t(
                        'chromeExtension.shotListAlt',
                        'Geschatte verkopen onder producten in de zoekresultaten van bol.com'
                    ),
                    t(
                        'chromeExtension.shotListCaption',
                        'Inschatting van verkopen en omzet per maand, direct onder elk product in de zoekresultaten.'
                    )
                )}
                {renderShot(
                    'chrome-ext-badge',
                    t('chromeExtension.shotBadgeAlt', 'De Bluemate verkoopbadge onder een product op bol.com'),
                    t(
                        'chromeExtension.shotBadgeCaption',
                        'De badge van dichtbij: geschatte verkopen en omzet per maand.'
                    )
                )}
            </section>
        );
    }

    function renderBottomCta() {
        return (
            <section className='bottom-cta'>
                <h2 className='section-heading'>
                    {t('chromeExtension.bottomTitle', 'Probeer de gratis Chrome-extensie')}
                </h2>
                <p className='bottom-cta-text'>
                    {t(
                        'chromeExtension.bottomText',
                        'Installeer de extensie op jouw Chrome browser. Bekijk de omzet van elk product op bol.com. Gratis en openbaar voor iedereen.'
                    )}
                </p>
                {renderStoreButton(t('chromeExtension.bottomButton', 'Download de gratis Chrome Extensie'))}
            </section>
        );
    }

    function renderShareSection() {
        return (
            <section className='share-section'>
                <h2 className='section-heading'>{t('chromeExtension.shareTitle', "Deel 'm met anderen")}</h2>
                <p className='share-section-text'>
                    {t(
                        'chromeExtension.shareText',
                        "Deze Chrome Extensie is gratis en publiek toegankelijk. Ken je iemand die dit ook wel een omzet schatting wilt zien bij elk product op bol? Deel de link met je vrienden of collega's."
                    )}
                </p>
                {renderShareButton()}
            </section>
        );
    }

    return (
        <div className='chrome-extension-page'>
            {renderHero()}
            {renderFeatures()}
            {renderScreenshots()}
            {renderBottomCta()}
            {renderShareSection()}
        </div>
    );
}
